import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { EmployeesActionsMenu } from '../EmployeesActionMenu/EmployeesActionMenu'
import { useEmployeesActionModal } from '../useEmployeesActionModal/useEmployeesActionModal'
import { AddWalletBalanceModal } from '../EmployeesModal/AddWalletBalanceModal'
import { UpdateMatchingBudgetModal } from '../EmployeesModal/UpdateMatchingBudgetModal'
import { AssignAchievementModal } from '../EmployeesModal/AssignAchievementModal'
import { RoutePath } from '../../../layout/Routes'

import {
  Button,
  Copy,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
  Tooltip
} from '@percent/lemonade'
import { formatMoney } from '@percent/utility'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { POSTHOG_FEATURE_FLAGS, useAuthState, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'
import { EmployeesTableProps } from './EmployeesTable.types'
import { UpdatedEmployee } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import { useSearchFilter } from '@percent/partner-dashboard/common/hooks/useSearchFilter/useSearchFilter'
import styles from './EmployeesTable.module.scss'

export function EmployeesTable({
  data,
  isLoading,
  totalResults,
  volunteeringRewardBudgetEnabled = false,
  errorMessage,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams,
  refresh
}: Readonly<EmployeesTableProps>) {
  const { partner } = useAuthState()
  const { t } = useTranslation()
  const {
    isAddWalletBalanceModalOpen,
    selectedEmployee,
    openAddWalletBalanceModal,
    closeAddWalletBalanceModal,
    isUpdateMatchingBudgetModalOpen,
    openUpdateMatchingBudgetModal,
    closeUpdateMatchingBudgetModal,
    isAssignAchievementModalOpen,
    openAssignAchievementBudgetModal,
    closeAssignAchievementBudgetModal
  } = useEmployeesActionModal()
  const navigate = useNavigate()
  const { userCan } = usePermissionCheck()

  const { searchValue, handleChange, handleKeyPress, handleClearValue } = useSearchFilter({
    queryParams: { query: queryParams?.filter },
    setQueryParams: params => setQueryParams?.({ ...queryParams, filter: params.query })
  })

  const showBulkWalletWizard = () => {
    navigate('/workplace-giving/employees/bulk-wallet-wizard')
  }

  const hasDonationWallet = partner?.scopes?.some(scope => scope.scopeName.includes('donation_wallet'))
  const hasDonationMatching = partner?.scopes?.some(scope => scope.scopeName.includes('donation_matching'))
  const hasGamification = partner?.scopes?.some(scope => scope.scopeName.includes('gamification'))
  const volunteeringRewardsVisible = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_VOLUNTEERING_REWARDS_VISIBILITY)
  const showVolunteeringRewardBudget = volunteeringRewardsVisible && volunteeringRewardBudgetEnabled

  const columns = [
    { id: t('table.header.fullName'), props: { width: '20%' } },
    { id: t('table.header.email'), props: { width: '30%' } },
    { id: t('table.header.team'), props: { width: '15%' } },
    ...(hasDonationWallet ? [{ id: t('table.header.currentWalletBalance'), props: { width: '15%' } }] : []),
    ...(hasDonationMatching
      ? [
          {
            id: t('table.header.matchingBudget'),
            content: (
              <Tooltip content={t('toolTip.workplaceGiving.matchingBudget')}>
                <span>{t('table.header.matchingBudget')}</span>
              </Tooltip>
            ),
            props: { width: '15%' }
          }
        ]
      : []),
    ...(showVolunteeringRewardBudget
      ? [{ id: t('table.header.volunteeringRewardsBudget'), props: { width: '15%' } }]
      : []),
    { id: '', props: { width: '5%' } }
  ]

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="no-results"
        emptyTableTitle={
          queryParams?.filter
            ? t('typography.noSearchTableListTitle', {
                searchTerm: queryParams?.filter
              })
            : t('typography.noListTitle', { listType: 'employees' })
        }
        emptyTableDescription={
          queryParams?.filter
            ? t('typography.noSearchTableListDescription')
            : t('typography.noListDescription', { listType: 'employees' })
        }
      >
        <TableSearchAndFilters>
          <SearchInput
            name="search"
            value={searchValue}
            onKeyDown={handleKeyPress}
            onChange={handleChange}
            handleClearValue={handleClearValue}
            placeholder={t('table.employeesListSearchPlaceholder')}
            status={searchValue.length >= 255 ? 'danger' : 'default'}
            statusMessage={t('errorMessage.shouldNotExceed255')}
          />
          {userCan('manageDonationWallet') && hasDonationWallet && (
            <Button data-testid="bulk-wallet-top-up" onClick={showBulkWalletWizard}>
              {t('button.bulkWalletTopUp')}
            </Button>
          )}
        </TableSearchAndFilters>
        <Table>
          <TableBody>
            {data?.map(employee => (
              <TableRow key={employee.id}>
                <TableCell>
                  <div>{employee.fullName}</div>
                </TableCell>
                <TableCell className={styles.emailCell}>
                  <Copy text={employee.email} className={styles.copyButton}>
                    <span className={styles.emailText}>{employee.email}</span>
                  </Copy>
                </TableCell>
                <TableCell>
                  {employee.team.length > 1 ? (
                    <Tooltip content={employee.team.join(', ')}>
                      {t('typography.teams', {
                        teamCount: employee.team.length
                      })}
                    </Tooltip>
                  ) : employee.team.length === 1 ? (
                    employee.team
                  ) : (
                    t('status.notAvailable')
                  )}
                </TableCell>
                {hasDonationWallet && (
                  <TableCell>
                    {employee?.wallet ? (
                      <span>{formatMoney(employee.wallet.balance, { hideDecimalsIfZero: true })}</span>
                    ) : (
                      t('status.notAvailable')
                    )}
                  </TableCell>
                )}
                {hasDonationMatching && (
                  <TableCell>
                    {employee?.matching ? (
                      <span>
                        {`${formatMoney(employee.matching.used, { hideDecimalsIfZero: true })}/${formatMoney(
                          employee.matching.total,
                          { hideDecimalsIfZero: true }
                        )}`}
                      </span>
                    ) : (
                      t('status.notAvailable')
                    )}
                  </TableCell>
                )}
                {showVolunteeringRewardBudget && (
                  <TableCell className={styles.rewardsCell}>
                    {employee.volunteeringRewardUsage
                      ? `${formatMoney(employee.volunteeringRewardUsage.earned, {
                          hideDecimalsIfZero: true
                        })} / ${formatMoney(employee.volunteeringRewardUsage.limit, { hideDecimalsIfZero: true })}`
                      : t('status.notAvailable')}
                  </TableCell>
                )}
                <TableCell>
                  <EmployeesActionsMenu
                    items={[
                      { key: 'seeDetails', label: t('dialog.employees.details.title') },
                      ...(userCan('manageDonationWallet') && hasDonationWallet
                        ? [{ key: 'add', label: t('dialog.employees.wallet.adjustTitle') }]
                        : []),
                      ...(hasDonationMatching && employee.matching
                        ? [{ key: 'update', label: t('dialog.employees.matching.title') }]
                        : []),
                      ...(hasGamification
                        ? [{ key: 'assignAchievement', label: t('dialog.employees.achievements.title') }]
                        : [])
                    ]}
                    handleSelect={(actionKey: string) => {
                      switch (actionKey) {
                        case 'seeDetails':
                          navigate(`${RoutePath.WORKPLACE_GIVING_EMPLOYEES_ID.replace(':employeeId', employee.id)}`)
                          break
                        case 'add':
                          openAddWalletBalanceModal(employee as UpdatedEmployee)
                          break
                        case 'update':
                          openUpdateMatchingBudgetModal(employee as UpdatedEmployee)
                          break
                        case 'assignAchievement':
                          openAssignAchievementBudgetModal(employee as UpdatedEmployee)
                          break
                      }
                    }}
                    currency={employee.currency}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      {selectedEmployee && (
        <AddWalletBalanceModal
          isOpen={isAddWalletBalanceModalOpen}
          employee={selectedEmployee}
          onClose={closeAddWalletBalanceModal}
          refresh={refresh}
        />
      )}
      {selectedEmployee && (
        <UpdateMatchingBudgetModal
          isOpen={isUpdateMatchingBudgetModalOpen}
          employee={selectedEmployee}
          onClose={closeUpdateMatchingBudgetModal}
          refresh={refresh}
        />
      )}
      {selectedEmployee && (
        <AssignAchievementModal
          isOpen={isAssignAchievementModalOpen}
          employee={selectedEmployee}
          onClose={closeAssignAchievementBudgetModal}
          refresh={refresh}
        />
      )}
    </>
  )
}
